import faker from 'faker';

faker.seed(42);

// ── Types ────────────────────────────────────────────────────────────────────

export type CaseStatus =
	| 'open'
	| 'closed'
	| 'reopened'
	| 'pending'
	| 'escalated';
export type DispositionDecision =
	| 'true_match'
	| 'false_match'
	| 'potential_match'
	| null;
export type AlertStatus =
	| 'open'
	| 'closed'
	| 'disposed'
	| 'pending'
	| 'hibernated';
export type ScoreLevel = 'low' | 'medium' | 'high' | 'very_high';
export type ModuleName = 'name_screening' | 'transaction_monitoring';
export type SlaStatus = 'breached' | 'on_track';

export type AmlUser = { id: string; name: string; role: string };

export type Case = {
	id: string;
	code: string;
	title: string;
	status: CaseStatus;
	disposition_decision: DispositionDecision;
	assignee: AmlUser | null;
	alert_count: number;
	case_age: number;
	created_at: string;
	priority: string;
	str_report_id: string | null;
};

export type Alert = {
	id: string;
	code: string;
	entity_name: string;
	customer_id: string;
	status: AlertStatus;
	module_name: ModuleName;
	score: { level: ScoreLevel; value: number };
	priority: string;
	assigned_to: AmlUser | null;
	sla_status: SlaStatus;
	created_at: string;
	case_id: string | null;
	disposition_decision: DispositionDecision;
	is_hibernated: boolean;
};

export type ScreenedEntity = {
	id: string;
	customer_name: string;
	customer_id: string;
	customer_type: string;
	date_of_birth: string;
	nationality: string;
	country: string;
	gender: string;
	address: string;
	pep_status: string;
};

export type WatchlistMatch = {
	id: string;
	name: string;
	score: number;
	category: string;
	pep_status: string;
	nationality: string;
	dob: string;
	aliases: string[];
	source: string;
	match_level: ScoreLevel;
};

export type WatchlistEntry = {
	id: string;
	uid: string;
	name: string;
	category: string;
	nationality: string;
	dob: string;
	pep_status: string;
	source: string;
	last_updated: string;
};

export type StrRecord = {
	id: string;
	code: string;
	case_id: string;
	case_code: string;
	submission_type: string;
	str_type: string;
	status: 'draft' | 'submitted' | 'acknowledged' | 'rejected';
	supervising_agency: string;
	filed_by: string;
	filed_at: string;
	trigger: string;
};

export type ScreeningSchedule = {
	id: string;
	name: string;
	type: 'batch' | 'continuous';
	frequency: string;
	last_run: string;
	next_run: string;
	status: 'active' | 'paused' | 'failed';
	records_count: number;
};

// ── Users ─────────────────────────────────────────────────────────────────────

export const MOCK_USERS: AmlUser[] = [
	{ id: 'user_1', name: 'Alex Morgan', role: 'admin' },
	{ id: 'user_2', name: 'Jordan Kim', role: 'manager' },
	{ id: 'user_3', name: 'Sam Rivera', role: 'senior_analyst' },
	{ id: 'user_4', name: 'Taylor Brooks', role: 'analyst' },
	{ id: 'user_5', name: 'Casey Chen', role: 'analyst' },
	{ id: 'user_6', name: 'Morgan Lee', role: 'analyst' },
	{ id: 'user_7', name: 'Riley Scott', role: 'analyst' },
	{ id: 'user_8', name: 'Quinn Davis', role: 'senior_analyst' },
];

// ── Cases ─────────────────────────────────────────────────────────────────────

const CASE_STATUSES: CaseStatus[] = [
	'open',
	'open',
	'open',
	'closed',
	'reopened',
	'pending',
	'escalated',
];
const DISPOSITIONS: DispositionDecision[] = [
	'true_match',
	'false_match',
	'potential_match',
	null,
];
const PRIORITIES = ['high', 'medium', 'low', 'very_high'];
const CASE_TITLES = [
	'PEP Screening Review',
	'Sanctions Match Investigation',
	'Transaction Monitoring Alert',
	'KYC Compliance Review',
	'Adverse Media Check',
	'Beneficial Ownership Review',
	'Enhanced Due Diligence',
	'Watchlist Hit Investigation',
];

export const MOCK_CASES: Case[] = Array.from({ length: 60 }, (_, i) => {
	const status = CASE_STATUSES[i % CASE_STATUSES.length];
	const isClosed = status === 'closed';
	const daysAgo = faker.datatype.number({ min: 1, max: 120 });
	const createdAt = new Date(Date.now() - daysAgo * 86400000).toISOString();
	return {
		id: `case_${i + 1}`,
		code: `CASE-${String(1000 + i).padStart(5, '0')}`,
		title: `${faker.name.findName()} — ${CASE_TITLES[i % CASE_TITLES.length]}`,
		status,
		disposition_decision: isClosed ? DISPOSITIONS[i % 3] : null,
		assignee: i % 5 === 0 ? null : MOCK_USERS[i % MOCK_USERS.length],
		alert_count: faker.datatype.number({ min: 1, max: 8 }),
		case_age: daysAgo,
		created_at: createdAt,
		priority: PRIORITIES[i % PRIORITIES.length],
		str_report_id: isClosed && i % 4 === 0 ? `STR-${1000 + i}` : null,
	};
});

// ── Alerts ─────────────────────────────────────────────────────────────────────

const ALERT_STATUSES: AlertStatus[] = [
	'open',
	'open',
	'open',
	'closed',
	'disposed',
	'pending',
	'hibernated',
];
const SCORE_LEVELS: ScoreLevel[] = ['low', 'medium', 'high', 'very_high'];
const MODULE_NAMES: ModuleName[] = [
	'name_screening',
	'name_screening',
	'transaction_monitoring',
];

function scoreLevelToValue(level: ScoreLevel): number {
	const base = { low: 30, medium: 55, high: 75, very_high: 90 };
	return base[level] + faker.datatype.number({ min: 0, max: 9 });
}

export const MOCK_ALERTS: Alert[] = Array.from({ length: 80 }, (_, i) => {
	const scoreLevel = SCORE_LEVELS[i % SCORE_LEVELS.length];
	const status = ALERT_STATUSES[i % ALERT_STATUSES.length];
	const isHibernated = status === 'hibernated';
	const isClosed = status === 'closed' || status === 'disposed';
	const caseIndex = i % MOCK_CASES.length;
	return {
		id: `alert_${i + 1}`,
		code: `ALT-${String(2000 + i).padStart(5, '0')}`,
		entity_name: faker.name.findName(),
		customer_id: `CUST-${faker.datatype.number({ min: 10000, max: 99999 })}`,
		status,
		module_name: MODULE_NAMES[i % MODULE_NAMES.length],
		score: { level: scoreLevel, value: scoreLevelToValue(scoreLevel) },
		priority: PRIORITIES[i % PRIORITIES.length],
		assigned_to: i % 6 === 0 ? null : MOCK_USERS[i % MOCK_USERS.length],
		sla_status: i % 3 === 0 ? 'breached' : 'on_track',
		created_at: new Date(
			Date.now() - faker.datatype.number({ min: 1, max: 90 }) * 86400000,
		).toISOString(),
		case_id: i % 3 === 0 ? MOCK_CASES[caseIndex].id : null,
		disposition_decision: isClosed ? DISPOSITIONS[i % 3] : null,
		is_hibernated: isHibernated,
	};
});

// ── Screened Entities ──────────────────────────────────────────────────────────

export const MOCK_ENTITIES: ScreenedEntity[] = MOCK_ALERTS.map((alert, i) => ({
	id: `entity_${i + 1}`,
	customer_name: alert.entity_name,
	customer_id: alert.customer_id,
	customer_type: i % 3 === 0 ? 'Corporate' : 'Individual',
	date_of_birth: faker.date.past(50).toISOString().split('T')[0],
	nationality: faker.address.country(),
	country: faker.address.country(),
	gender: i % 2 === 0 ? 'Male' : 'Female',
	address: `${faker.address.streetAddress()}, ${faker.address.city()}, ${faker.address.country()}`,
	pep_status: i % 4 === 0 ? 'PEP' : 'Non-PEP',
}));

// ── Watchlist Matches ──────────────────────────────────────────────────────────

const CATEGORIES = [
	'Sanctions',
	'PEP',
	'Adverse Media',
	'Law Enforcement',
	'Regulatory',
];
const SOURCES = [
	'OFAC',
	'UN Sanctions',
	'EU Sanctions',
	'Interpol',
	'World-Check',
	'Dow Jones',
];

export function generateMatchesForAlert(alertId: string): WatchlistMatch[] {
	const seed = alertId.charCodeAt(alertId.length - 1);
	const count = (seed % 4) + 1;
	return Array.from({ length: count }, (_, i) => {
		const score = 55 + i * 10 + (seed % 15);
		const level: ScoreLevel =
			score >= 85
				? 'very_high'
				: score >= 70
					? 'high'
					: score >= 55
						? 'medium'
						: 'low';
		return {
			id: `match_${alertId}_${i}`,
			name: faker.name.findName(),
			score,
			category: CATEGORIES[i % CATEGORIES.length],
			pep_status: i % 2 === 0 ? 'PEP' : 'Non-PEP',
			nationality: faker.address.country(),
			dob: faker.date.past(70).toISOString().split('T')[0],
			aliases: Array.from({ length: i + 1 }, () => faker.name.findName()),
			source: SOURCES[i % SOURCES.length],
			match_level: level,
		};
	});
}

// ── Watchlist Entries ──────────────────────────────────────────────────────────

export const MOCK_WATCHLIST: WatchlistEntry[] = Array.from(
	{ length: 100 },
	(_, i) => ({
		id: `wl_${i + 1}`,
		uid: `UID-${faker.datatype.uuid().slice(0, 8).toUpperCase()}`,
		name: faker.name.findName(),
		category: CATEGORIES[i % CATEGORIES.length],
		nationality: faker.address.country(),
		dob: faker.date.past(70).toISOString().split('T')[0],
		pep_status: i % 3 === 0 ? 'PEP' : 'Non-PEP',
		source: SOURCES[i % SOURCES.length],
		last_updated: new Date(
			Date.now() - faker.datatype.number({ min: 1, max: 365 }) * 86400000,
		).toISOString(),
	}),
);

// ── STR Records ───────────────────────────────────────────────────────────────

const STR_TYPES = ['CTR', 'SAR', 'STR-Local'];
const SUBMISSION_TYPES = ['Initial', 'Amendment', 'Supplemental'];
const STR_TRIGGERS = [
	'Unusual Transaction',
	'PEP Match',
	'Sanctions Hit',
	'Structuring',
	'Layering',
];
const STR_STATUSES: StrRecord['status'][] = [
	'draft',
	'submitted',
	'acknowledged',
	'rejected',
];
const AGENCIES = ['AMLC', 'FinCEN', 'FCA', 'MAS', 'FINTRAC'];

export const MOCK_STRS: StrRecord[] = Array.from({ length: 30 }, (_, i) => ({
	id: `str_${i + 1}`,
	code: `STR-${String(3000 + i).padStart(5, '0')}`,
	case_id: MOCK_CASES[i % MOCK_CASES.length].id,
	case_code: MOCK_CASES[i % MOCK_CASES.length].code,
	submission_type: SUBMISSION_TYPES[i % SUBMISSION_TYPES.length],
	str_type: STR_TYPES[i % STR_TYPES.length],
	status: STR_STATUSES[i % STR_STATUSES.length],
	supervising_agency: AGENCIES[i % AGENCIES.length],
	filed_by: MOCK_USERS[i % MOCK_USERS.length].name,
	filed_at: new Date(
		Date.now() - faker.datatype.number({ min: 1, max: 180 }) * 86400000,
	).toISOString(),
	trigger: STR_TRIGGERS[i % STR_TRIGGERS.length],
}));

// ── Screening Schedules ────────────────────────────────────────────────────────

const SCHEDULE_STATUSES: ScreeningSchedule['status'][] = [
	'active',
	'active',
	'paused',
	'failed',
];

export const MOCK_SCHEDULES: ScreeningSchedule[] = Array.from(
	{ length: 8 },
	(_, i) => {
		const lastRun = new Date(
			Date.now() - faker.datatype.number({ min: 1, max: 10 }) * 86400000,
		);
		const nextRun = new Date(
			lastRun.getTime() + (i % 2 === 0 ? 86400000 : 3600000 * 6),
		);
		return {
			id: `schedule_${i + 1}`,
			name: `Schedule ${String.fromCharCode(65 + i)} — ${i % 2 === 0 ? 'Batch' : 'Continuous'} Screen`,
			type: i % 2 === 0 ? 'batch' : 'continuous',
			frequency: i % 2 === 0 ? 'Daily' : 'Every 6 hours',
			last_run: lastRun.toISOString(),
			next_run: nextRun.toISOString(),
			status: SCHEDULE_STATUSES[i % SCHEDULE_STATUSES.length],
			records_count: faker.datatype.number({ min: 100, max: 50000 }),
		};
	},
);

// ── Admin Stats ───────────────────────────────────────────────────────────────

export const MOCK_ADMIN_STATS = {
	total_open_cases: MOCK_CASES.filter(
		(c) => c.status === 'open' || c.status === 'reopened',
	).length,
	unassigned_cases: MOCK_CASES.filter((c) => c.assignee === null).length,
	escalated_cases: MOCK_CASES.filter((c) => c.status === 'escalated').length,
	pending_review: MOCK_CASES.filter((c) => c.status === 'pending').length,
	total_alerts: MOCK_ALERTS.length,
	open_alerts: MOCK_ALERTS.filter((a) => a.status === 'open').length,
	breached_sla: MOCK_ALERTS.filter((a) => a.sla_status === 'breached').length,
	high_priority_alerts: MOCK_ALERTS.filter(
		(a) => a.score.level === 'high' || a.score.level === 'very_high',
	).length,
};

// ── Case Alerts helper ────────────────────────────────────────────────────────

export function getAlertsForCase(caseId: string): Alert[] {
	const linked = MOCK_ALERTS.filter((a) => a.case_id === caseId);
	if (linked.length > 0) return linked.slice(0, 5);
	// Always return at least 1 alert
	return [
		MOCK_ALERTS[
			Math.abs(caseId.charCodeAt(caseId.length - 1)) % MOCK_ALERTS.length
		],
	];
}

export function getCaseById(caseId: string): Case | undefined {
	return MOCK_CASES.find((c) => c.id === caseId);
}

export function getAlertById(alertId: string): Alert | undefined {
	return MOCK_ALERTS.find((a) => a.id === alertId);
}

export function getEntityForAlert(alertId: string): ScreenedEntity | undefined {
	const index = MOCK_ALERTS.findIndex((a) => a.id === alertId);
	return index >= 0 ? MOCK_ENTITIES[index] : undefined;
}

// ── Timeline entries ──────────────────────────────────────────────────────────

export type TimelineEvent = {
	id: string;
	type:
		| 'created'
		| 'assigned'
		| 'commented'
		| 'status_changed'
		| 'escalated'
		| 'closed'
		| 'reopened';
	actor: string;
	timestamp: string;
	description: string;
};

export function getTimelineForCase(caseId: string): TimelineEvent[] {
	const seed = caseId.charCodeAt(caseId.length - 1);
	const count = (seed % 5) + 3;
	const events: TimelineEvent[] = [
		{
			id: `evt_${caseId}_0`,
			type: 'created',
			actor: MOCK_USERS[seed % MOCK_USERS.length].name,
			timestamp: new Date(Date.now() - (seed + 5) * 86400000).toISOString(),
			description: 'Case created from alert',
		},
	];
	for (let i = 1; i < count; i++) {
		const types: TimelineEvent['type'][] = [
			'assigned',
			'commented',
			'status_changed',
		];
		events.push({
			id: `evt_${caseId}_${i}`,
			type: types[i % types.length],
			actor: MOCK_USERS[(seed + i) % MOCK_USERS.length].name,
			timestamp: new Date(Date.now() - (seed + 5 - i) * 86400000).toISOString(),
			description: [
				'Case assigned to analyst for review',
				'Added note: Customer profile reviewed, pending additional documentation',
				'Status updated to pending review',
				'Requested additional KYC documents',
			][i % 4],
		});
	}
	return events;
}
